import { memo, ReactElement, useCallback } from 'react';
import {
    Flex,
    Icon,
    cssVar,
    Button,
    Spinner,
    Popover,
    PopoverTrigger,
    PopoverContent,
} from '@cad-arc';
import { EsriMapLayersDropdown } from './esri-map-layers-dropdown';
import { EsriMapFiltersDropdown } from './esri-map-filters-dropdown';
import { EsriMapFiltersDropdownDeprecated } from './esri-map-filters-dropdown-deprecated';
import { WebMap } from '../constructors';
import {
    LayerId,
    EsriMapFilterOptions,
    EsriMapFilterState,
    DeprecatedEsriMapFilterOptions,
    DeprecatedEsriMapFilterState,
} from '../types';
import { colors } from '../../colors/colors';

type EsriMapDropdownsProps = {
    webMap: WebMap | undefined;
    isMapLoading: boolean;
    filterOptions: EsriMapFilterOptions;
    filterState: EsriMapFilterState;
    setFilterState: (filterState: EsriMapFilterState) => void;
    saveFilters?: () => void;
    visibleLayersIds: LayerId[];
    setVisibleLayersIds: (layersId: LayerId[]) => void;
    saveLayers?: () => void;
    rollOutPersistWorkspaceMapFiltersEnabled?: boolean;
    deprecatedFilterOptions: DeprecatedEsriMapFilterOptions;
    deprecatedFilterState: DeprecatedEsriMapFilterState;
    setDeprecatedFilterState: (filterState: DeprecatedEsriMapFilterState) => void;
};

const getCommonButtonStyles = (hasOptionsSelected: boolean) => ({
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: hasOptionsSelected
        ? cssVar('arc.colors.selected.emphasis')
        : colors.mapButtonBackground,
    color: hasOptionsSelected
        ? cssVar('arc.colors.selected.emphasisContent')
        : colors.mapButtonForeground,
});

export const EsriMapFiltersAndLayersDropdowns = memo(
    ({
        webMap,
        isMapLoading = true,
        setVisibleLayersIds,
        visibleLayersIds,
        filterOptions,
        filterState,
        setFilterState,
        rollOutPersistWorkspaceMapFiltersEnabled,
        deprecatedFilterOptions,
        deprecatedFilterState,
        setDeprecatedFilterState,
        saveFilters,
        saveLayers,
    }: EsriMapDropdownsProps): ReactElement => {
        const hasFiltersSelected =
            !isMapLoading &&
            Object.values(
                (rollOutPersistWorkspaceMapFiltersEnabled ? filterState : deprecatedFilterState) ??
                    {}
            ).some((selectedFilters) => !!selectedFilters?.length);
        const hasLayersSelected = !!visibleLayersIds?.length;
        const handleCloseFiltersPopover = useCallback(() => {
            saveFilters?.();
        }, [saveFilters]);
        const onCloseLayersPopover = useCallback(() => {
            saveLayers?.();
        }, [saveLayers]);
        return (
            <Flex position="absolute" top="12px" right="12px" alignItems="center" gap="5px">
                {isMapLoading ? (
                    <Flex w="100vw" h="100vh" alignItems="center" justifyContent="center">
                        <Spinner size="lg" />
                    </Flex>
                ) : (
                    <>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="solid"
                                    style={getCommonButtonStyles(hasFiltersSelected)}
                                    data-testid="MAP_FILTERS_BUTTON"
                                >
                                    <Icon icon="Filter" /> Filters
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent
                                onEscapeKeyDown={handleCloseFiltersPopover}
                                onInteractOutside={handleCloseFiltersPopover}
                            >
                                {rollOutPersistWorkspaceMapFiltersEnabled ? (
                                    <EsriMapFiltersDropdown
                                        filterOptions={filterOptions}
                                        filterState={filterState}
                                        setFilterState={setFilterState}
                                    />
                                ) : (
                                    <EsriMapFiltersDropdownDeprecated
                                        filterOptions={deprecatedFilterOptions}
                                        filterState={deprecatedFilterState}
                                        setFilterState={setDeprecatedFilterState}
                                    />
                                )}
                            </PopoverContent>
                        </Popover>
                        <EsriMapLayersDropdown
                            commonPopoverButtonStyles={getCommonButtonStyles(hasLayersSelected)}
                            webMap={webMap}
                            setVisibleLayersIds={setVisibleLayersIds}
                            visibleLayersIds={visibleLayersIds}
                            onCloseLayersPopover={onCloseLayersPopover}
                        />
                    </>
                )}
            </Flex>
        );
    }
);
